import type {
  ApiRequestObject,
  BaseApiListResponse,
  BaseApiRequest,
} from '../../../lib/flotiq-client/types/api-request';
import type { HookOptions } from './types';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getQueryCacheKey } from '../../../lib/queryClientHelpers';

import useSelectedSpace from '../../useSelectedSpace';
import useToken from '../../useToken';

export interface ListingPagination {
  count: number;
  current_page: number;
  total_count: number;
  total_pages: number;
}

interface HookListingObjectResult<T extends BaseApiListResponse<any>> {
  data: T extends { ok: true } ? T['body']['data'] : [];
  pagination: ListingPagination;
  isLoading: boolean;
  status: number;
  errors: any;
  reload: () => Promise<void>;
  isFetching: boolean;
}

type HookListingArrayResult<T extends BaseApiListResponse<any>> = [
  data: T extends { ok: true } ? T['body']['data'] : [],
  pagination: ListingPagination,
  isLoading: boolean,
  status: number,
  errors: any,
  reload: () => Promise<void>,
  isFetching: boolean,
];

type HookListingResult<T extends BaseApiListResponse<any>> =
  HookListingObjectResult<T> & HookListingArrayResult<T>;

/**
 * Hook builder for listing entities
 */
export function createListingHook<T extends BaseApiListResponse<any>>(
  getEntities: BaseApiRequest<T>,
  cacheKeyPrefix?: string | string[],
  defaultParams: ApiRequestObject = {},
  defaultOptions: HookOptions = {},
) {
  return (
    params = defaultParams,
    hookOptions = defaultOptions,
    cacheKey = '',
  ) => {
    const jwt = useToken();
    const { space } = useSelectedSpace();
    const {
      checkStatus = true,
      cancelUnfinishedRequests = true,
      pause = false,
      staleTime = 0,
      gcTime = 5 * 60 * 1000, //5 minutes
    } = hookOptions || {};

    const queryKey = useMemo(
      () => getQueryCacheKey(cacheKeyPrefix, cacheKey, params, 'list', space),
      [cacheKey, params, space],
    );

    const {
      isLoading: requestLoading,
      data: response,
      error: errors,
      refetch: reload,
      isPlaceholderData,
      isFetching,
    } = useQuery({
      queryKey,
      queryFn: async ({ signal }) => {
        const result = await getEntities(
          jwt,
          space,
          params,
          cancelUnfinishedRequests ? { signal } : {},
        );
        if (checkStatus && !result.ok) {
          throw parseResponseErrors(result.body, result.status);
        }
        return result;
      },
      enabled: !!jwt && !pause,
      staleTime,
      gcTime,
      retry: 0,
      refetchInterval: hookOptions?.refetchInterval ?? false,
      refetchIntervalInBackground:
        hookOptions?.refetchIntervalInBackground ?? false,
    });

    const isLoading = requestLoading || isPlaceholderData;

    const { data, status, pagination } = useMemo(
      () =>
        response?.body
          ? {
              data: Array.isArray(response.body)
                ? response.body
                : response.body.data,
              status: response?.status,
              pagination: {
                total_pages: response.body.total_pages,
                total_count: response.body.total_count,
                count: response.body.count,
                current_page: response.body.current_page,
              },
            }
          : {
              data: [],
              pagination: {},
              status: response?.status || (errors as any)?.status || 0,
            },
      [errors, response?.body, response?.status],
    );

    const result = [
      data,
      pagination,
      isLoading,
      status,
      errors,
      reload,
      isFetching,
    ];
    Object.assign(result, {
      data,
      pagination,
      isLoading,
      status,
      errors,
      reload,
      isFetching,
    });
    return result as HookListingResult<T>;
  };
}
